import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import useAPI from "../api/hooks/useAPI";
import { filterKeys } from "../api/queryKeys";
import { deepEqual } from "../utils/utils";

export const useSavedFilters = ({
  page,
  appliedFilters,
  setAppliedFilters,
  hasUserInteracted = true,
  setHasUserInteracted,
}) => {
  const queryClient = useQueryClient();
  const {
    saveFilters,
    loadFilters,
    deleteSavedFilter,
    updateSavedFilter,
    trackEvent,
  } = useAPI();

  // Local state
  const [activeFilterId, setActiveFilterId] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFilter, setEditingFilter] = useState(null);

  // Load saved filters query
  const { data: savedFilters = [] } = useQuery({
    queryKey: filterKeys.savedFilters(page),
    queryFn: () => loadFilters(page),
    select: (data) => data.data || [],
  });

  // Save filter mutation
  const saveFilterMutation = useMutation({
    mutationFn: ({ savedFilter }) => saveFilters(page, savedFilter),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: filterKeys.savedFilters(page),
      });
      setShowSaveModal(false);
      trackEvent(`User saved a filter in ${page} page`);
    },
    onError: (error) => {
      console.error("Error saving filter:", error);
    },
  });

  // Update filter mutation
  const updateFilterMutation = useMutation({
    mutationFn: ({ filterId, updateData }) =>
      updateSavedFilter(page, filterId, updateData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: filterKeys.savedFilters(page),
      });
      setShowEditModal(false);
      setEditingFilter(null);
      trackEvent(`User updated saved filter in ${page} page`);
    },
    onError: (error) => {
      console.error("Error updating filter:", error);
    },
  });

  // Delete filter mutation
  const deleteFilterMutation = useMutation({
    mutationFn: ({ filterId }) => deleteSavedFilter(page, filterId),
    onSuccess: (_, { filterId }) => {
      queryClient.invalidateQueries({
        queryKey: filterKeys.savedFilters(page),
      });
      if (activeFilterId === filterId) {
        setActiveFilterId(null);
      }
      trackEvent(`User deleted saved filter in ${page} page`);
    },
    onError: (error) => {
      console.error("Error deleting filter:", error);
    },
  });

  // Handler functions
  const handleSaveFilter = useCallback(
    (filterData) => {
      const filterId = `filter_${Date.now()}`;
      const savedFilter = {
        id: filterId,
        name: filterData.name,
        description: filterData.description,
        filters: { ...appliedFilters },
        is_default: filterData.is_default,
      };

      saveFilterMutation.mutate({ savedFilter });

      if (filterData.is_default) {
        setActiveFilterId(filterId);
      }
    },
    [appliedFilters, saveFilterMutation]
  );

  const handleApplyFilter = useCallback(
    (filter) => {
      setAppliedFilters(filter.filters);
      setActiveFilterId(filter.id);
      if (setHasUserInteracted) {
        setHasUserInteracted(true);
      }
      trackEvent(`User applied saved filter in ${page} page`);
    },
    [setAppliedFilters, setHasUserInteracted, trackEvent, page]
  );

  const handleEditFilter = useCallback((filter) => {
    setEditingFilter(filter);
    setShowEditModal(true);
  }, []);

  const handleUpdateFilter = useCallback(
    (updateData) => {
      if (!editingFilter) return;

      updateFilterMutation.mutate({
        filterId: editingFilter.id,
        updateData,
      });
    },
    [editingFilter, updateFilterMutation]
  );

  const handleDeleteFilter = useCallback(
    (filter) => {
      deleteFilterMutation.mutate({
        filterId: filter.id,
      });
    },
    [deleteFilterMutation]
  );

  const handleUnselectFilter = useCallback(
    (resetFunction) => {
      if (resetFunction) {
        resetFunction();
      }
      setActiveFilterId(null);
      if (setHasUserInteracted) {
        setHasUserInteracted(true);
      }
      trackEvent(`User unselected saved filter in ${page} page`);
    },
    [setHasUserInteracted, trackEvent, page]
  );

  // Handle loaded saved filters - apply default filter
  useEffect(() => {
    if (savedFilters && savedFilters.length > 0 && !hasUserInteracted) {
      const defaultFilter = savedFilters.find((f) => f.is_default);
      if (defaultFilter && !activeFilterId) {
        handleApplyFilter(defaultFilter);
      }
    }
  }, [savedFilters, activeFilterId, handleApplyFilter, hasUserInteracted]);

  // Bidirectional filter sync
  useEffect(() => {
    if (savedFilters.length > 0) {
      // Reset active filter if applied filters don't match the active saved filter
      if (activeFilterId) {
        const activeFilter = savedFilters.find((f) => f.id === activeFilterId);
        if (activeFilter) {
          const filtersMatch = deepEqual(appliedFilters, activeFilter.filters);
          if (!filtersMatch) {
            setActiveFilterId(null);
            if (setHasUserInteracted) {
              setHasUserInteracted(true);
            }
          }
        }
      }

      // Set active filter if applied filters match a saved filter
      if (!activeFilterId) {
        const matchingFilter = savedFilters.find((filter) =>
          deepEqual(appliedFilters, filter.filters)
        );

        if (matchingFilter) {
          setActiveFilterId(matchingFilter.id);
        }
      }
    }
  }, [appliedFilters, activeFilterId, savedFilters, setHasUserInteracted]);

  return {
    // State
    savedFilters,
    activeFilterId,
    showSaveModal,
    showEditModal,
    editingFilter,

    // State setters
    setActiveFilterId,
    setShowSaveModal,
    setShowEditModal,
    setEditingFilter,

    // Handlers
    handleSaveFilter,
    handleApplyFilter,
    handleEditFilter,
    handleUpdateFilter,
    handleDeleteFilter,
    handleUnselectFilter,

    // Mutation states
    isSaving: saveFilterMutation.isPending,
    isUpdating: updateFilterMutation.isPending,
    isDeleting: deleteFilterMutation.isPending,
  };
};

export default useSavedFilters;
